import { ReactNode, useEffect } from 'react';
import { X, Filter, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MobileFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onReset?: () => void;
  children: ReactNode;
  title?: string;
  hasActiveFilters?: boolean;
}

export function MobileFilterDrawer({
  isOpen,
  onClose,
  onReset,
  children,
  title = 'Filters',
  hasActiveFilters = false,
}: MobileFilterDrawerProps) {
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="filter-drawer-backdrop md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <div
        className={cn(
          'filter-drawer md:hidden',
          isOpen ? 'filter-drawer-open' : 'filter-drawer-closed'
        )}
      >
        {/* Handle bar */}
        <div className="flex justify-center py-3">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <h2 className="font-heading text-lg font-semibold text-xtravirt-dark">
              {title}
            </h2>
            {hasActiveFilters && (
              <span className="px-2 py-0.5 text-xs font-medium bg-xtravirt-cyan text-white rounded-full">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onReset && (
              <button
                onClick={onReset}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors touch-target"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors touch-target"
              aria-label="Close filters"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {children}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="btn-primary w-full touch-target"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
}

// Filter button to trigger the drawer
interface FilterButtonProps {
  onClick: () => void;
  hasActiveFilters?: boolean;
  activeCount?: number;
}

export function MobileFilterButton({ onClick, hasActiveFilters, activeCount }: FilterButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'md:hidden flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors touch-target',
        hasActiveFilters
          ? 'border-xtravirt-cyan bg-xtravirt-cyan/10 text-xtravirt-cyan'
          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
      )}
    >
      <Filter className="h-4 w-4" />
      <span className="text-sm font-medium">Filters</span>
      {activeCount !== undefined && activeCount > 0 && (
        <span className="px-1.5 py-0.5 text-xs font-medium bg-xtravirt-cyan text-white rounded-full min-w-[20px] text-center">
          {activeCount}
        </span>
      )}
    </button>
  );
}

// Filter field component for consistent styling
interface FilterFieldProps {
  label: string;
  children: ReactNode;
}

export function FilterField({ label, children }: FilterFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
